import feathersClient from '@/feathers-client'

/**
 * Normalizes Feathers response to always be an array
 * @param {Array|Object} response - API response from Feathers
 * @returns {Array} Normalized array of items
 */
const normalizeResponse = (response) => {
  if (Array.isArray(response)) {
    return response
  }
  return response?.data || []
}

/**
 * Returns the set of compassSubCategory ids that are currently enabled for this compass.
 * Action ratings whose action lives in a non-enabled subcategory must not contribute to
 * any score sum, regardless of which compassCategoryId the rating is filed under.
 */
const getEnabledCompassSubCategoryIds = async (compassId) => {
  const response = await feathersClient.service('compass-sub-categories').find({
    query: { compassId, isEnabled: true, $select: ['id'] },
  })
  return new Set(normalizeResponse(response).map(sc => sc.id))
}

/**
 * Gets action ratings for multiple compassCategories in a compass
 * @param {number} frameworkId - The ID of the framework
 * @param {string|number} compassId - The ID of the compass
 * @param {Array} compassCategories - Array of category objects
 * @param {number} compassVersion - The version of the compass
 * @returns {Promise<Array<number>>} Array of category action ratings
 */
export const getCategoryActionRatings = async (frameworkId, compassId, compassCategories, compassVersion = 2) => {
  // Validate required parameters
  if (!compassId || !frameworkId || !compassCategories || compassCategories.length === 0) {
    return compassCategories.map(() => 0)
  }

  // Handle version 1 compass - need to get old-style ratings
  if (compassVersion === 1) {
    try {
      const oldCompassCatsResponse = await feathersClient.service('rating-data').find({
        query: {
          frameworkId,
          compassId,
        },
      })

      const oldCompassCats = normalizeResponse(oldCompassCatsResponse)

      if (oldCompassCats.length === 0) {
        return compassCategories.map(() => 0)
      }

      return compassCategories.map(category => {
        const match = oldCompassCats.find(cat => cat.id === category.categoryId)
        return match ? match.value : 0
      })
    } catch (error) {
      console.error('Error getting old compass ratings:', error)
      return compassCategories.map(() => 0)
    }
  }

  // For version 2+ compasses
  try {
    if (frameworkId !== 4 && frameworkId !== 3) {
      // For framework 1, get all action ratings in one request
      const actionCategoryIds = compassCategories.map(category => category.id)
      const [allActionRatingsResponse, enabledSubCategoryIds] = await Promise.all([
        feathersClient.service('action-ratings').find({
          query: {
            compassId,
            compassCategoryId: {
              $in: actionCategoryIds,
            },
          },
        }),
        getEnabledCompassSubCategoryIds(compassId),
      ])

      const allActionRatings = normalizeResponse(allActionRatingsResponse)
      if (allActionRatings.length === 0) {
        return compassCategories.map(() => 0)
      }

      // Resolve which compassActionIds live in an enabled compass-sub-category.
      const referencedActionIds = [...new Set(allActionRatings.map(r => r.compassActionId))]
      const compassActionsResponse = referencedActionIds.length > 0
        ? await feathersClient.service('compass-actions').find({
          query: {
            id: { $in: referencedActionIds },
            $select: ['id', 'compassSubCategoryId'],
          },
        })
        : []
      const enabledActionIds = new Set(
        normalizeResponse(compassActionsResponse)
          .filter(a => enabledSubCategoryIds.has(a.compassSubCategoryId))
          .map(a => a.id),
      )

      // Group the ratings by category and sum them, ignoring ratings from
      // actions whose compass-sub-category is not enabled.
      const actionRatings = compassCategories.map(category => {
        const categoryRatings = allActionRatings.filter(rating => (
          rating.compassCategoryId === category.id &&
          enabledActionIds.has(rating.compassActionId)
        ))
        return categoryRatings.reduce((sum, rating) => sum + rating.rating, 0)
      })
      return actionRatings
    } else if (frameworkId === 3) {
      // For framework 3:
      // 1. Get all subcategory mappings in one request
      const allMappingsResponse = await feathersClient.service('sub-compassCategories-defaults').find({
        query: {
          frameworksCategoryId: {
            $in: compassCategories.map(category => category.id),
          },
        },
      })

      const allMappings = normalizeResponse(allMappingsResponse)

      if (allMappings.length === 0) {
        return compassCategories.map(() => 0)
      }

      // Create a map of category IDs to their mapped subcategory IDs
      const categoryToSubCategoriesMap = {}
      allMappings.forEach(mapping => {
        if (!categoryToSubCategoriesMap[mapping.frameworksCategoryId]) {
          categoryToSubCategoriesMap[mapping.frameworksCategoryId] = []
        }
        categoryToSubCategoriesMap[mapping.frameworksCategoryId].push(mapping.subCategoryId)
      })

      // Get all relevant subcategory IDs
      const allMappedSubCatIds = allMappings.map(mapping => mapping.subCategoryId)

      // 2. Get all compass actions for these subcategories in one request
      const [allCompassActionsResponse, enabledSubCategoryIds] = await Promise.all([
        feathersClient.service('compass-actions').find({
          query: {
            compassId,
            subCategoryId: {
              $in: allMappedSubCatIds,
            },
          },
        }),
        getEnabledCompassSubCategoryIds(compassId),
      ])

      const allCompassActions = normalizeResponse(allCompassActionsResponse)
        .filter(action => enabledSubCategoryIds.has(action.compassSubCategoryId))

      if (allCompassActions.length === 0) {
        return compassCategories.map(() => 0)
      }

      // Create a map of subcategory IDs to the compass action IDs they contain
      const subCategoryToActionIdsMap = {}
      allCompassActions.forEach(action => {
        if (!subCategoryToActionIdsMap[action.subCategoryId]) {
          subCategoryToActionIdsMap[action.subCategoryId] = []
        }
        subCategoryToActionIdsMap[action.subCategoryId].push(action.id)
      })

      // Get all compass action IDs
      const allCompassActionIds = allCompassActions.map(action => action.id)

      // 3. Get all ratings for these actions in one request
      const allRatingsResponse = await feathersClient.service('action-ratings').find({
        query: {
          compassActionId: {
            $in: allCompassActionIds,
          },
        },
      })

      const allRatings = normalizeResponse(allRatingsResponse)

      if (allRatings.length === 0) {
        return compassCategories.map(() => 0)
      }

      // Calculate ratings for each category
      return compassCategories.map(category => {
        const categorySubCatIds = categoryToSubCategoriesMap[category.id] || []

        // Get all action IDs associated with this category's subcategories
        const categoryActionIds = categorySubCatIds.flatMap(
          subCatId => subCategoryToActionIdsMap[subCatId] || [],
        )

        // Sum up ratings for all these actions
        const categoryRatings = allRatings.filter(
          rating => categoryActionIds.includes(rating.compassActionId),
        )

        return categoryRatings.reduce((sum, rating) => sum + rating.rating, 0)
      })
    } else {
      // For other frameworks, return zeros
      return compassCategories.map(() => 0)
    }
  } catch (error) {
    console.error('Error getting category action ratings:', error)
    return compassCategories.map(() => 0)
  }
}
